import React from "react";
import {
  Avatar,
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Stack,
  useColorModeValue,
  Table,
  Tbody,
  Tr,
  Td,
  Tooltip,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  useBreakpointValue,
  useColorMode,
  Switch,
  Box,
  Text,
  SliderMark,
} from "@chakra-ui/react";
import { useButtonProps, useUserProfile, useUserSubscriptionDetails } from "hooks";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { UpgradePlanButton } from "../subscription/SubscriptionUpgrade";
import { getAllocatedQuantity, getUsedQuantity } from "api/usage/models/FeatureUsage";
import { useSelector } from "react-redux";
import type { RootState } from "state/rootReducer";
import type { FeatureUsage } from "api/usage/models/FeatureUsage";
import { BlockSectionHeader } from "screens/content/contentView/previewSection/BlockSectionHeader";

interface UsageIndicatorProps {
  usage: FeatureUsage;
  colorModeShade: string;
}

const UsageIndicator: React.FC<UsageIndicatorProps> = ({ usage, colorModeShade }) => {
  const [showTooltip, setShowTooltip] = React.useState(false);
  const casedLabel = usage.handle
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
  const used = getUsedQuantity(usage);
  const allocated = getAllocatedQuantity(usage);
  const percentage = allocated > 0 ? Math.min((used / allocated) * 100, 100) : 0;

  return (
    <Tr>
      <Td paddingInlineStart={0}>{casedLabel}</Td>
      <Td paddingInlineStart={0} textAlign={"right"}>
        <>
          {(() => {
            switch (usage.type) {
              case "on_off_component":
                return (
                  <Tooltip placement="top" isOpen={showTooltip} label={`Feature is ${usage.isOn ? "enabled" : "disabled"}`}>
                    <Switch
                      size="sm"
                      cursor={"pointer"}
                      isChecked={usage.isOn}
                      isReadOnly
                      onMouseEnter={() => setShowTooltip(true)}
                      onMouseLeave={() => setShowTooltip(false)}
                    />
                  </Tooltip>
                );
              case "quantity_based_component":
                return <Text>{`${used} purchased`}</Text>;
              default:
                return (
                  <Slider
                    width={["100%", "100%", "20rem"]}
                    isReadOnly
                    value={used}
                    min={0}
                    max={allocated}
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}>
                    {(allocated < used || used === 0) && (
                      <SliderMark
                        value={allocated}
                        boxSize={6}
                        borderRadius="full"
                        mt="-13px"
                        p="1px"
                        bgColor={percentage === 0 ? `gray.500` : percentage < 90 ? `teal.500` : `orange.500`}>
                        <Text fontSize="xs" color="white" fontWeight="bold" textAlign="center" lineHeight={"1.4rem"}>
                          {allocated > 99 ? `99+` : allocated}
                        </Text>
                      </SliderMark>
                    )}
                    <SliderTrack height="10px" borderRadius={"full"}>
                      <SliderFilledTrack
                        bg={
                          percentage === 0
                            ? `gray.${colorModeShade}`
                            : percentage < 90
                            ? `teal.${colorModeShade}`
                            : `orange.${colorModeShade}`
                        }
                      />
                    </SliderTrack>
                    <Tooltip placement="top" isOpen={showTooltip} label={`You have used ${used} of ${allocated} ${casedLabel}`}>
                      <SliderThumb boxSize={6}>
                        <Box
                          boxSize={5}
                          borderRadius="full"
                          bgColor={percentage === 0 ? `gray.500` : percentage < 90 ? `teal.500` : `orange.500`}>
                          <Text fontSize="xs" color="white" fontWeight="bold" textAlign="center" lineHeight={"1.2rem"}>
                            {used}
                          </Text>
                        </Box>
                      </SliderThumb>
                    </Tooltip>
                  </Slider>
                );
            }
          })()}
        </>
      </Td>
    </Tr>
  );
};

export const Profile = () => {
  const { id, fullName, firstName, lastName, email, avatarUrl, isProfileUpdatePending, updateUser } = useUserProfile();
  const secondaryButtonStyle = useButtonProps("sm", "secondary");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const subscriptionInfo = useUserSubscriptionDetails();
  const { colorMode } = useColorMode();
  const colorModeShade = colorMode === "dark" ? "900" : "200";
  const isMobile = useBreakpointValue({ base: true, md: false }, { fallback: "md", ssr: false });
  const usageData = useSelector((state: RootState) => state.usage?.featureUsage || {});

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    watch,
  } = useForm<{
    firstName: string;
    lastName: string;
  }>({
    defaultValues: {
      firstName: firstName || "",
      lastName: lastName || "",
    },
    mode: "onChange",
  });

  const watchedFields = watch();
  const hasChanged = watchedFields.firstName !== (firstName || "") || watchedFields.lastName !== (lastName || "");

  const onSubmit = useCallback(
    (fields: { firstName: string; lastName: string }) => {
      updateUser(fields);
    },
    [updateUser]
  );

  const { ref: firstNameRef, ...firstNameRest } = register("firstName", { required: true });
  const { ref: lastNameRef, ...lastNameRes } = register("lastName", { required: true });

  return (
    <Flex width="100%" flexDirection="column" justify="space-between" mb="1rem">
      <Stack direction="row" width="100%" spacing="2rem">
        {!isMobile && <Avatar name={fullName} src={avatarUrl} boxSize="4.5rem" />}
        <Stack spacing="2rem" width="100%">
          <Box pb="2rem">
            <BlockSectionHeader title="Profile Details">
              <Button
                className="ch-settings-save-button"
                {...secondaryButtonStyle}
                onClick={handleSubmit(onSubmit)}
                isLoading={isProfileUpdatePending}
                isDisabled={!hasChanged || !isValid}>
                Update Profile
              </Button>
            </BlockSectionHeader>
            <Stack spacing="1.5rem">
              <Stack direction="row" spacing="2rem">
                <FormControl isInvalid={!!errors.firstName}>
                  <FormLabel>First name</FormLabel>
                  <Input
                    {...firstNameRest}
                    id="firstName"
                    placeholder="First name"
                    ref={firstNameRef}
                    borderColor={errors.firstName ? "red.500" : borderColor}
                  />
                  {errors.firstName && (
                    <FormErrorMessage fontSize="xs" mt="0.3rem">
                      First name cannot be blank.
                    </FormErrorMessage>
                  )}
                </FormControl>
                <FormControl isInvalid={!!errors.lastName}>
                  <FormLabel>Last name</FormLabel>
                  <Input
                    {...lastNameRes}
                    id="lastName"
                    placeholder="Last name"
                    ref={lastNameRef}
                    borderColor={errors.lastName ? "red.500" : borderColor}
                  />
                  {errors.lastName && (
                    <FormErrorMessage fontSize="xs" mt="0.3rem">
                      Last name cannot be blank.
                    </FormErrorMessage>
                  )}
                </FormControl>
              </Stack>
              <Stack direction="row" spacing="2rem">
                <FormControl id="email-address">
                  <FormLabel>Email address</FormLabel>
                  <Input placeholder="Email address" value={email} isDisabled />
                </FormControl>
                <FormControl id="charli-id">
                  <FormLabel>Charli ID</FormLabel>
                  <Input placeholder="Charli ID" value={id} isDisabled />
                </FormControl>
              </Stack>
            </Stack>
          </Box>
          <Stack spacing="2rem">
            <Box>
              <BlockSectionHeader title="Subscription Details">
                <UpgradePlanButton buttonSize="xs" style={{ marginRight: "1rem" }} />
              </BlockSectionHeader>
              <Table variant="simple" size="sm">
                <Tbody>
                  <Tr>
                    <Td paddingInlineStart={0}>Current Subscription</Td>
                    <Td paddingInlineStart={0} className="ch-subscription-plan" textAlign={"right"}>
                      {subscriptionInfo.plan}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td paddingInlineStart={0}>Next Billing Date</Td>
                    <Td paddingInlineStart={0} className="ch-subscription-billing" textAlign={"right"}>
                      {subscriptionInfo.nextBillingDate
                        ? new Intl.DateTimeFormat(undefined, {
                            dateStyle: "long",
                            timeZone: "UTC",
                          }).format(new Date(subscriptionInfo.nextBillingDate))
                        : "N/A"}
                    </Td>
                  </Tr>
                  <Tr>
                    <Td paddingInlineStart={0}>Status</Td>
                    <Td paddingInlineStart={0} className="ch-subscription-status" textAlign={"right"}>
                      <Button
                        className="ch-subscription-status"
                        {...secondaryButtonStyle}
                        _hover={{}}
                        _active={{}}
                        _focus={{}}
                        cursor={"default"}
                        size="xs"
                        color="gray.100"
                        bgColor={`${subscriptionInfo.status === "active" ? "green" : "blue"}.400`}>
                        {subscriptionInfo.status.toUpperCase()}
                      </Button>
                    </Td>
                  </Tr>
                </Tbody>
              </Table>
            </Box>
            <Box>
              <BlockSectionHeader title="Feature Usage" />
              <Table variant="simple" size="sm">
                <Tbody>
                  {Object.entries(usageData).map(([handle, usage]) => (
                    <UsageIndicator key={handle} usage={usage} colorModeShade={colorModeShade} />
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Stack>
        </Stack>
      </Stack>
    </Flex>
  );
};
